import { ConfigProvider, Spin } from "antd";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import NotFound from "./component/notfound";
import { logout, setUser } from "./redux/slices/userslice";
import Routing from "./routes";
import ProtectedRoute from "./routes/protectedroute";
import LoginScreen from "./screens/loginscreen";
import ForgotPassword from "./screens/forgotpassword";
import WebPreview from "./screens/webScreenPreview";
import api from "./config/axiosInstance";
import {
  SingleTabBlocker,
  useSingleTabGuard,
  BROADCAST_CHANNEL_NAME,
} from "./utils/singleTabGuard";
import { requestGeolocation, resetGeolocation } from "./utils/geolocation";
import { useSessionStream } from "./utils/sessionStream";
import { installBodyImageFallback } from "./utils/mediaFallback";

function App() {
  const User = useSelector((state: any) => state.User);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const { isBlocked } = useSingleTabGuard();

  // Instant logout when this session is revoked elsewhere (single-session).
  useSessionStream(Boolean(User?.isAuthenticated && User?.user?.email));

  const rehydrateSession = async () => {
    // Skip all API calls if the user never had a session in this browser.
    // This avoids a guaranteed auth/me → 401 → auth/refresh → 401 double-hit
    // on every cold start when the user is clearly unauthenticated.
    if (!localStorage.getItem('_session')) {
      dispatch(logout({}));
      setLoading(false);
      return;
    }

    try {
      const response = await api.get("auth/me");
      if (response.data?.user) {
        dispatch(setUser(response.data.user));
        requestGeolocation();
      } else {
        dispatch(logout({}));
        resetGeolocation();
      }
    } catch {
      dispatch(logout({}));
      resetGeolocation();
    } finally {
      setLoading(false);
    }
  };

  // Cover EVERY raw <img> in the admin at once: a single document-level
  // capture-phase error listener swaps any failed server image to its S3 backup.
  // No need to wire onError on each render site individually.
  useEffect(() => {
    installBodyImageFallback();
  }, []);

  useEffect(() => {
    rehydrateSession();
  }, []);

  // Listen for forced-logout broadcast from other tabs (e.g. when admin blocks
  // this user and the active tab fires forceLogout() which notifies siblings).
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    let channel: BroadcastChannel;
    try {
      channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      channel.onmessage = (event) => {
        if (event.data?.type === "FORCED_LOGOUT") {
          dispatch(logout({}));
          // No window.location redirect — ProtectedRoute handles navigation
          // via React Router when isAuthenticated becomes false.
        }
      };
    } catch {
      return;
    }
    return () => {
      try {
        channel?.close();
      } catch {}
    };
  }, [dispatch]);

  if (isBlocked) {
    return <SingleTabBlocker />;
  }

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#0055A6",
          fontFamily: "Montserrat-Medium",
          borderRadius: 4,
          colorTextPlaceholder: "#888c99",
        },
      }}
    >
      <BrowserRouter>
        <Routes>
          <Route index element={<LoginScreen />} />
          <Route path="/" element={<LoginScreen />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/WebPreview" element={<WebPreview />} />
          <Route
            path="/Auth/*"
            element={
              <ProtectedRoute
                isSignedIn={User?.isAuthenticated && User?.user?.email}
              >
                <Routing />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;
